import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import RewardNotifications from './RewardNotifications';
import StatsPanel from './StatsPanel';
import { useGlobalContext } from '../../data/GlobalContext';
import CountdownTimer, { getKeepPushingRound } from './CountdownTimer';
import Leaderboard from './Leaderboard';
import RadialNavigation from './RadialNavigation';
import FortuneRushTermsAndConditions from './FortuneRushTermsAndConditions';
import JiliGames from './JiliGames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSearch } from '@fortawesome/free-solid-svg-icons';
import { getKeepPushingMessage } from './CountdownTimer';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();

        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    return isMobile;
};

const JiliRushGame = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('leaderboard');
    const [showTerms, setShowTerms] = useState(false);
    const [showJiliGames, setShowJiliGames] = useState(false);
    const [keepPushingData, setKeepPushingData] = useState(
        getKeepPushingRound()
    );
    const [showKeepPushingMessage, setShowKeepPushingMessage] = useState(
        getKeepPushingMessage()
    );
    const [toast, setShowToast] = useState({
        show: false,
        message: '',
        type: 'success',
    });

    const handleToast = (message, type = 'success') => {
        setShowToast({
            message,
            type,
            show: true,
        });

        setTimeout(() => {
            setShowToast((prev) => ({
                ...prev,
                show: false,
            }));
        }, 3000);
    };

    const {
        username,
        jiliFortuneRushWeek,
        jiliFortuneRushLeaderboard: entries,
        jiliFortuneRushLeaderboardLoading: loading,
        jiliFortuneRushLeaderboardError: error,
    } = useGlobalContext();

    const TEST_MODE = false;
    const TEST_RUSH_WEEK = 5;

    const rawRushWeek = TEST_MODE
        ? TEST_RUSH_WEEK
        : Number(jiliFortuneRushWeek);

    const isValidRushWeek =
        Number.isInteger(rawRushWeek) && rawRushWeek >= 0 && rawRushWeek <= 4;

    const rushWeek = isValidRushWeek ? rawRushWeek : null;

    const isStartsInWeek = rushWeek === 0;
    const isKeepPushingWeek = [2, 4].includes(rushWeek);

    const isRushWeekNotFound = rushWeek === null;

    const isMobile = useIsMobile();

    const MotionDiv = isMobile ? 'div' : motion.div;
    const MotionImg = isMobile ? 'img' : motion.img;
    const MotionButton = isMobile ? 'button' : motion.button;

    const isPlayerInLeaderboard = entries?.some((item) => item.isYou);

    const playerRank = entries?.find((item) => item.isYou)?.rank;

    const getFreeSpinsReward = (rank) => {
        if (!rank) return 0;

        if (rank === 1) return 200;

        if (rank >= 2 && rank <= 3) return 100;

        if (rank >= 4 && rank <= 10) return 50;

        if (rank >= 11 && rank <= 30) return 20;

        return 0;
    };

    const playerFreeSpins = getFreeSpinsReward(playerRank);

    const tabs = [
        {
            id: 'raffle',
            label: 'Raffle Entries',
            image: '/images/game/jili-rush/JILI_RAFFLE_ENTRIES.webp',
            imageActive:
                '/images/game/jili-rush/JILI_RAFFLE_ENTRIES-ACTIVE.webp',
        },
        {
            id: 'leaderboard',
            label: 'Leaderboard',
            image: '/images/game/jili-rush/JILI_QUEST_LEADERBOARD.webp',
            imageActive:
                '/images/game/jili-rush/JILI_QUEST_LEADERBOARD-ACTIVE.webp',
        },
        {
            id: 'spin',
            label: 'Spin',
            image: '/images/game/jili-rush/JILI_SPIN_SF_TAB.webp',
            imageActive: '/images/game/jili-rush/JILI_SPIN_SF_TAB-ACTIVE.webp',
        },
    ];

    const tabVariants = {
        initial: {
            opacity: 0,
            y: 15,
        },
        animate: {
            opacity: 1,
            y: 0,
        },
        exit: {
            opacity: 0,
            y: -15,
        },
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setKeepPushingData(getKeepPushingRound());
            setShowKeepPushingMessage(getKeepPushingMessage());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <Helmet>
                <title>JILI Fortune Rush</title>
            </Helmet>

            {toast.show &&
                createPortal(
                    <div className={`toast toast-jili-rush ${toast.type}`}>
                        {toast.message} ✅
                    </div>,
                    document.body
                )}

            <MotionDiv
                className="fortune-modal-overlay"
                {...(!isMobile && {
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0 },
                    transition: { duration: 0.2 },
                })}
            >
                <div className="fortune-lollipop">
                    <img
                        src="/images/game/jili-rush/JILI_RUSH-LOLLIPOP.webp"
                        alt=""
                    />
                </div>

                {/* User Profile */}
                <div className="fortune-username">
                    <div className="panel-desktop">
                        <img
                            src="/images/game/jili-rush/JILI-USER.webp"
                            alt=""
                        />
                    </div>
                    <div className="panel-mobile">
                        <img
                            src="/images/game/jili-rush/FGH-USER-FORTUNE_RUSH.webp?v=1"
                            alt=""
                        />
                    </div>
                    <div className="fortune-user-details">
                        <div className="fortune-user-name">{username}</div>

                        <div className="fortune-user-rank">
                            <div className="fortune-user-rank">
                                {entries?.find((item) => item.isYou)?.rank
                                    ? `Current Rank: #${entries.find((item) => item.isYou).rank}`
                                    : 'Not Ranked Yet'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="fortune-rush--logo">
                    <div className="fortune-rush--logo-container">
                        {!showTerms && (
                            <img
                                onClick={onClose}
                                src="/images/game/jili-rush/JILI-FORTUNE-RUSH_HOME.webp"
                                alt=""
                            />
                        )}

                        {!showTerms && (
                            <img
                                src="/images/game/jili-rush/JILI-FORTUNE-RUSH_T&C.webp"
                                alt=""
                                onClick={() => setShowTerms(true)}
                            />
                        )}

                        {!showTerms && !showJiliGames && (
                            <img
                                onClick={() => setShowJiliGames(true)}
                                src="/images/game/jili-rush/JILI-FORTUNE-SLOT.webp"
                                alt=""
                            />
                        )}
                    </div>
                </div>
                {showTerms ? (
                    <FortuneRushTermsAndConditions
                        key="terms"
                        onBack={() => setShowTerms(false)}
                    />
                ) : (
                    <MotionDiv
                        className="fortune-modal"
                        {...(!isMobile && {
                            initial: {
                                opacity: 0,
                                scale: 0.85,
                                y: 30,
                            },
                            animate: {
                                opacity: 1,
                                scale: 1,
                                y: 0,
                            },
                            exit: {
                                opacity: 0,
                                scale: 0.9,
                                y: 20,
                            },
                            transition: {
                                type: 'spring',
                                stiffness: 300,
                                damping: 25,
                            },
                        })}
                    >
                        <div className="fortune-modal-logo">
                            <img
                                src="/images/game/jili-rush/JILI_QUEST_LOGO.webp"
                                alt=""
                            />
                        </div>
                        {showJiliGames && (
                            <footer className="jili-games-close">
                                <button
                                    type="button"
                                    className="fr-tnc-button"
                                    onClick={() => {
                                        setShowJiliGames(false);
                                        setActiveTab('leaderboard');
                                    }}
                                >
                                    <span>
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </button>
                            </footer>
                        )}

                        <motion.img
                            src="/images/game/jili-rush/JILI_QUEST_CONTAINER.webp"
                            className="fortune-modal-frame"
                            alt=""
                            draggable="false"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.3 }}
                        />

                        <div className="fortune-modal-content">
                            {showJiliGames && <JiliGames />}
                            {!showJiliGames && (
                                <div className="fortune-modal-tabs">
                                    {tabs.map((tab, index) => {
                                        const isActive = activeTab === tab.id;

                                        return (
                                            <MotionButton
                                                key={tab.id}
                                                type="button"
                                                role="tab"
                                                aria-selected={isActive}
                                                className={`jili-rush--tab ${
                                                    isActive
                                                        ? 'jili-rush--tab-active'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    setActiveTab(tab.id)
                                                }
                                                {...(!isMobile && {
                                                    initial: {
                                                        opacity: 0,
                                                        y: -10,
                                                    },
                                                    animate: {
                                                        opacity: 1,
                                                        y: 0,
                                                        scale: isActive
                                                            ? 1.05
                                                            : 1,
                                                    },
                                                    whileHover: {
                                                        scale: 1.05,
                                                        y: -2,
                                                    },
                                                    whileTap: {
                                                        scale: 0.95,
                                                    },
                                                    transition: {
                                                        delay: index * 0.07,
                                                        type: 'spring',
                                                        stiffness: 400,
                                                        damping: 20,
                                                    },
                                                })}
                                            >
                                                <MotionImg
                                                    src={
                                                        isActive
                                                            ? tab.imageActive
                                                            : tab.image
                                                    }
                                                    alt={tab.label}
                                                    {...(!isMobile && {
                                                        animate: {
                                                            scale: isActive
                                                                ? 1.03
                                                                : 1,
                                                        },
                                                        transition: {
                                                            type: 'spring',
                                                            stiffness: 400,
                                                            damping: 20,
                                                        },
                                                    })}
                                                />
                                            </MotionButton>
                                        );
                                    })}
                                </div>
                            )}

                            {activeTab === 'raffle' && (
                                <motion.div
                                    key="raffle"
                                    variants={tabVariants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    className="fortune-mod"
                                    transition={{ duration: 0.2 }}
                                >
                                    <StatsPanel />
                                </motion.div>
                            )}
                            {!showJiliGames && (
                                <div className="fortune-modal-body">
                                    <AnimatePresence mode="wait">
                                        {activeTab === 'leaderboard' && (
                                            <>
                                                {isStartsInWeek && (
                                                    <div className="jili-rush-starts-in">
                                                        <img
                                                            src="/images/game/jili-rush/JILI-STARTS-IN.webp"
                                                            alt=""
                                                        />
                                                        <CountdownTimer upcomingTitle="Get ready to complete!" />
                                                    </div>
                                                )}

                                                {isPlayerInLeaderboard &&
                                                    isKeepPushingWeek && (
                                                        <motion.div
                                                            key="leaderboard"
                                                            variants={
                                                                tabVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                            }}
                                                            className="fortune-modal--leaderboard"
                                                        >
                                                            <motion.div
                                                                className="fortune-leaderboard"
                                                                initial={{
                                                                    opacity: 0,
                                                                    scale: 0.97,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    scale: 1,
                                                                }}
                                                                transition={{
                                                                    delay: 0.1,
                                                                    duration: 0.25,
                                                                }}
                                                            >
                                                                <Leaderboard />
                                                            </motion.div>

                                                            <motion.div
                                                                className="fortune-message"
                                                                initial={{
                                                                    opacity: 0,
                                                                    scale: 0.97,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    scale: 1,
                                                                }}
                                                                transition={{
                                                                    delay: 0.1,
                                                                    duration: 0.25,
                                                                }}
                                                            >
                                                                <div className="fortune-message--congratulatory">
                                                                    <img
                                                                        src="/images/game/jili-rush/JILI-CONGRATULATORY-COPY.webp"
                                                                        alt=""
                                                                    />
                                                                    <div className="number-style">
                                                                        {
                                                                            playerFreeSpins
                                                                        }
                                                                    </div>
                                                                    <div className="fortune-freespin">
                                                                        Free
                                                                        Spins
                                                                    </div>
                                                                </div>

                                                                <CountdownTimer />
                                                            </motion.div>
                                                        </motion.div>
                                                    )}
                                                {isRushWeekNotFound && (
                                                    <div className="jili-rush-data-not-found">
                                                        <img
                                                            src="/images/game/jili-rush/JILI-RUSH-NO-DATA.webp"
                                                            alt=""
                                                        />
                                                        <p>
                                                            Check back soon to
                                                            see the latest
                                                            leaderboard
                                                            standings.
                                                        </p>
                                                    </div>
                                                )}
                                                {isKeepPushingWeek &&
                                                    showKeepPushingMessage &&
                                                    !isPlayerInLeaderboard && (
                                                        <motion.div
                                                            key="keep-pushing"
                                                            initial={{
                                                                opacity: 0,
                                                                scale: 0.97,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                scale: 1,
                                                            }}
                                                            transition={{
                                                                delay: 0.1,
                                                                duration: 0.25,
                                                            }}
                                                            className="jili-rush-keep-push"
                                                        >
                                                            <img
                                                                src="/images/game/jili-rush/JILI-KEEP-PUSHING.webp"
                                                                alt=""
                                                            />

                                                            <div className="jili-rush-keep-push--sched">
                                                                <div>
                                                                    <img
                                                                        src="/images/game/jili-rush/JILI-CALENDAR.webp"
                                                                        alt=""
                                                                    />
                                                                </div>

                                                                <div>
                                                                    Next Round
                                                                    Cutoff:
                                                                    <div className="date">
                                                                        Oct 15
                                                                        (12:00PM)
                                                                        - Oct 22
                                                                        (11:59AM)
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}

                                                {!isStartsInWeek &&
                                                    !isRushWeekNotFound &&
                                                    !isKeepPushingWeek && (
                                                        <motion.div
                                                            key="leaderboard"
                                                            variants={
                                                                tabVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                            }}
                                                            className="fortune-modal--leaderboard"
                                                        >
                                                            <motion.div
                                                                className="fortune-leaderboard"
                                                                initial={{
                                                                    opacity: 0,
                                                                    scale: 0.97,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    scale: 1,
                                                                }}
                                                                transition={{
                                                                    delay: 0.1,
                                                                    duration: 0.25,
                                                                }}
                                                            >
                                                                <Leaderboard />
                                                            </motion.div>

                                                            <motion.div
                                                                className="fortune-message"
                                                                initial={{
                                                                    opacity: 0,
                                                                    scale: 0.97,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    scale: 1,
                                                                }}
                                                                transition={{
                                                                    delay: 0.1,
                                                                    duration: 0.25,
                                                                }}
                                                            >
                                                                <div className="fortune-message--banner">
                                                                    <img
                                                                        src="/images/game/jili-rush/JILI_RUSH_MODAL.webp"
                                                                        alt=""
                                                                    />
                                                                    <p>
                                                                        {entries?.find(
                                                                            (
                                                                                item
                                                                            ) =>
                                                                                item.isYou
                                                                        )?.rank
                                                                            ? `Current Rank: #${entries.find((item) => item.isYou).rank}`
                                                                            : 'Not Ranked Yet'}
                                                                    </p>
                                                                </div>
                                                                <CountdownTimer />
                                                            </motion.div>
                                                        </motion.div>
                                                    )}
                                            </>
                                        )}

                                        {activeTab === 'spin' && (
                                            <RewardNotifications
                                                onToast={handleToast}
                                            />
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>
                    </MotionDiv>
                )}
                <div className="radial-navigation">
                    {!showTerms && (
                        <RadialNavigation
                            onHome={onClose}
                            onTerms={() => setShowTerms(true)}
                            onJili={() => setShowJiliGames(true)}
                        />
                    )}
                </div>

                <div className="fortune-lollipop-right">
                    <img
                        src="/images/game/jili-rush/JILI_RUSH-LOLLIPOP2.webp"
                        alt=""
                    />
                </div>
            </MotionDiv>
        </>
    );
};

export default JiliRushGame;
